import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlassTitle } from '../components/GlassTitle';
import { getPublicPricing } from '../services/api.broker';
import {
  Activity, ArrowRight, TrendingUp, Shield, Zap, BarChart3, Bot,
  CheckCircle2, ChevronRight, Sparkles, Loader2, Star, Quote,
  Bell, FlaskConical, Users, Lock, RefreshCw,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../components/ui/accordion';
import { useAuth } from '../context/AuthContext';


// ─────────────────────────────────────────────────────────────────────────────
// Static content
// ─────────────────────────────────────────────────────────────────────────────

const PILLARS = [
  {
    icon: TrendingUp,
    title: 'Multi-Broker Automation',
    description:
      'Connect TopstepX, Apex, TradeDay, and more from one dashboard. Change prop firms without rebuilding your setup.',
    accent: 'text-blue-400',
    bg: 'bg-blue-500/10 group-hover:bg-blue-500/20',
    badge: null,
  },
  {
    icon: Bell,
    title: 'Strategy Alerts & Notifications',
    description:
      'Get instant alerts on trade entries, drawdown warnings, and daily P&L summaries — pushed to any device the moment they happen.',
    accent: 'text-purple-400',
    bg: 'bg-purple-500/10 group-hover:bg-purple-500/20',
    badge: null,
  },
  {
    icon: Bot,
    title: 'AI Support + Human Escalation',
    description:
      'An AI assistant resolves most questions instantly (L1). Complex issues are escalated to a real person (L2) — no ticketing queue, no waiting.',
    accent: 'text-amber-400',
    bg: 'bg-amber-500/10 group-hover:bg-amber-500/20',
    badge: null,
  },
  {
    icon: Shield,
    title: 'Per-Account Risk Controls',
    description:
      'Independent daily loss limits, max contract sizes, and kill switches — per account. Enforcement runs at the order level, not just as a warning.',
    accent: 'text-green-400',
    bg: 'bg-green-500/10 group-hover:bg-green-500/20',
    badge: null,
  },
  {
    icon: FlaskConical,
    title: 'Backtesting & Paper Trading',
    description:
      'Validate your strategy against historical data before risking capital. Paper trading mode lets you verify execution logic risk-free.',
    accent: 'text-rose-400',
    bg: 'bg-rose-500/10 group-hover:bg-rose-500/20',
    badge: null,
  },
];

const STEPS = [
  {
    step: '01',
    title: 'Subscribe',
    description:
      'Pick a plan that matches your account count. Free plan is free forever — no credit card required to start.',
  },
  {
    step: '02',
    title: 'Connect Your Broker',
    description:
      'Enter your prop firm API credentials in the setup wizard. TopstepX, Apex, and more are supported. Takes under 5 minutes.',
  },
  {
    step: '03',
    title: 'Trade & Automate',
    description:
      'Configure your strategy rules, set risk limits per account, and enable auto-trading. TradoPro handles execution 24/7 with a full audit trail.',
  },
];

const TESTIMONIALS = [
  {
    quote:
      'Passed my $150k TopstepX evaluation in 18 days. The strategy fired at exactly the right entries — no hesitation, no second-guessing. I couldn\'t have managed that manually across three time zones.',
    name: '@ryan_futures',
    tag: 'Funded Trader — $150k TopstepX',
  },
  {
    quote:
      'My daily drawdown limit has never been breached since switching to TradoPro. The per-account risk controls are non-negotiable now — it\'s like having a risk manager on every account, around the clock.',
    name: '@carolineT',
    tag: 'Prop Firm Trader — 3 funded accounts',
  },
  {
    quote:
      'Running 5 funded accounts from the Pro plan — one dashboard, zero manual errors. The AI support answered my setup question at 2 am, instantly. No other platform comes close.',
    name: '@mike_prop_desk',
    tag: 'Multi-Account Trader — Pro Plan',
  },
];

const FAQ_ITEMS = [
  {
    q: 'Does automation violate my prop firm\'s rules?',
    a: 'TradoPro sends orders through your broker\'s official API — the same channel as clicking Buy/Sell yourself. Most prop firms (Topstep, Apex, TradeDay) explicitly permit automation through official APIs. We recommend confirming with your specific firm, but API-based automated order entry is standard practice in prop trading.',
  },
  {
    q: 'Do I need to leave my computer on 24/7?',
    a: 'No. TradoPro is cloud-hosted — your strategies run on our servers even when your device is off. If your broker requires a local desktop bridge for order routing (common with some WebSocket-based brokers), TradoPro supports a lightweight local bridge. Most workflows run fully in the cloud with no local software required.',
  },
  {
    q: 'Which brokers and prop firms are supported?',
    a: 'TopstepX is supported natively via the ProjectX API, with additional brokers being added regularly. If your prop firm\'s platform offers an official REST or WebSocket API, we can typically add support. Contact us to check compatibility for your specific setup.',
  },
  {
    q: 'How does per-account risk protection actually work?',
    a: 'Every account gets its own independent daily drawdown limit, max contract size, and an emergency kill switch. These rules are enforced at the order level — not just monitored as warnings. When any threshold is reached, TradoPro immediately stops order submission on that account and sends you an alert.',
  },
  {
    q: 'Can I cancel anytime? What happens to my account?',
    a: 'Yes — cancel from your account settings at any time, no penalty and no forms. Your plan stays active until the end of the current billing period, then stops. No automatic renewal, no surprise charges. Your data and strategy configurations are retained for 30 days after cancellation.',
  },
  {
    q: 'What is the refund policy?',
    a: 'Every paid plan comes with a 14-day money-back guarantee. If you\'re not satisfied within the first 14 days of a new subscription, contact support for a full refund — no questions asked. After 14 days, plans are non-refundable but you may cancel to prevent future charges.',
  },
  {
    q: 'Is my API key and trading data secure?',
    a: 'Broker API credentials are encrypted at rest. All data in transit is protected with TLS 1.3. We never store payment card details — all billing is processed directly by Stripe. We do not sell, share, or use your trading data for any purpose other than executing your own strategies.',
  },
];

const STATS = [
  { value: '2,000+', label: 'Funded Traders' },
  { value: '99.9%', label: 'Uptime SLA' },
  { value: '4+', label: 'Prop Firms' },
  { value: '14-day', label: 'Money-Back Guarantee' },
];

const TRUST_FIRMS = ['Topstep', 'TopstepX', 'Apex Trader Group', 'TradeDay', 'FTMO'];

const NAV_ITEMS = [
  { id: 'features',     label: 'Features' },
  { id: 'how-it-works', label: 'How it Works' },
  { id: 'pricing',      label: 'Pricing' },
  { id: 'faq',          label: 'FAQ' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

const LandingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();

  const [scrolled, setScrolled]           = useState(false);
  const [showScrollHint, setShowScrollHint] = useState(true);
  const [activeSection, setActiveSection] = useState('hero');
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [pricingPlans, setPricingPlans]   = useState([]);
  const [loadingPricing, setLoadingPricing] = useState(true);
  const [selectedPlan, setSelectedPlan]   = useState(null);

  const sectionsRef = useRef({});

  // ── Fix document overflow for landing-page scroll ────────────────────────
  useLayoutEffect(() => {
    const appRoot = document.getElementById('root');
    const saved = {
      docOverflow:  document.documentElement.style.overflow,
      bodyOverflow: document.body.style.overflow,
      bodyHeight:   document.body.style.height,
      rootOverflow: appRoot?.style.overflow ?? '',
      rootHeight:   appRoot?.style.height   ?? '',
    };
    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow  = 'visible';
    document.body.style.height    = 'auto';
    if (appRoot) { appRoot.style.overflow = 'visible'; appRoot.style.height = 'auto'; }
    return () => {
      document.documentElement.style.overflow = saved.docOverflow;
      document.body.style.overflow  = saved.bodyOverflow;
      document.body.style.height    = saved.bodyHeight;
      if (appRoot) { appRoot.style.overflow = saved.rootOverflow; appRoot.style.height = saved.rootHeight; }
    };
  }, []);

  // ── Fetch pricing from single public endpoint ────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const res = await getPublicPricing();
        const plans = res.data.plans || [];
        setPricingPlans(plans);
        const popular = plans.find(p => p.is_popular);
        if (popular) setSelectedPlan(popular.name);
      } catch {
        // Fallback UI only — prices match DB defaults
        const fallback = [
          { name: 'FREE',       display_name: 'Free',       price_monthly: 0,    price_yearly: 0,     yearly_discount_percent: 0,  description: 'Perfect for getting started',   features: ['1 Trading Account', '2 Strategies', 'Basic Charts', 'Manual Trading'],                                                    is_popular: false, cta: 'Get Started' },
          { name: 'BASIC',      display_name: 'Basic',      price_monthly: 29,   price_yearly: 290,   yearly_discount_percent: 17, description: 'For active traders',            features: ['3 Trading Accounts', '10 Strategies', 'All Instruments', 'AI Chatbot', 'Strategy Alerts', 'Email Support'],           is_popular: false, cta: 'Start Free Trial' },
          { name: 'PRO',        display_name: 'Pro',        price_monthly: 79,   price_yearly: 790,   yearly_discount_percent: 17, description: 'For serious prop traders',       features: ['5 Trading Accounts', '20 Strategies', 'All Instruments', 'AI Analysis', 'Auto-Trading', 'Backtesting', 'Priority Support'], is_popular: true,  cta: 'Start Free Trial' },
          { name: 'ENTERPRISE', display_name: 'Enterprise', price_monthly: 199,  price_yearly: 1990,  yearly_discount_percent: 17, description: 'For professional firms',         features: ['Unlimited Accounts', 'Unlimited Strategies', 'API Access', 'Custom Strategies', 'Dedicated Support'],              is_popular: false, cta: 'Contact Sales' },
        ];
        setPricingPlans(fallback);
        setSelectedPlan('PRO');
      } finally {
        setLoadingPricing(false);
      }
    })();
  }, []);

  // ── Scroll state ─────────────────────────────────────────────────────────
  const handleScroll = useCallback(() => {
    const y = window.scrollY;
    setScrolled(y > 50);
    if (y > 80) setShowScrollHint(false);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // ── Scroll-spy ───────────────────────────────────────────────────────────
  useEffect(() => {
    const navIds = NAV_ITEMS.map(n => n.id);
    const inView = new Set();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          const id = navIds.find(k => sectionsRef.current[k] === entry.target);
          if (!id) return;
          entry.isIntersecting ? inView.add(id) : inView.delete(id);
        });
        const active = navIds.find(id => inView.has(id));
        if (active) setActiveSection(active);
        else if (window.scrollY < 120) setActiveSection('hero');
      },
      { rootMargin: '-72px 0px -45% 0px', threshold: 0 }
    );
    navIds.forEach(id => { const el = sectionsRef.current[id]; if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, []);

  // ── Smooth scroll ────────────────────────────────────────────────────────
  const scrollToSection = (id) => {
    const el = sectionsRef.current[id];
    if (!el) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: reduced ? 'instant' : 'smooth', block: 'start' });
  };

  // ── Auth-aware subscription routing ─────────────────────────────────────
  const handleSubscribe = useCallback((planName) => {
    if (!isAuthenticated) {
      const dest = planName ? `/subscription?plan=${planName}` : '/subscription';
      navigate(`/login?next=${encodeURIComponent(dest)}`);
      return;
    }
    const hasPaid = user?.subscription_plan && user.subscription_plan !== 'FREE';
    navigate(hasPaid ? '/dashboard' : (planName ? `/subscription?plan=${planName}` : '/subscription'));
  }, [isAuthenticated, user, navigate]);

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">

      {/* ── Navbar ── */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-black/90 backdrop-blur-xl border-b border-white/10' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => scrollToSection('hero')}>
            <Activity className="w-8 h-8 text-blue-500" />
            <span className="text-xl font-semibold tracking-tight">TradoPro</span>
          </div>

          <div className="hidden md:flex items-center gap-1">
            {NAV_ITEMS.map(item => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`px-4 py-2 text-sm rounded-lg transition-all duration-200 ${
                  activeSection === item.id
                    ? 'text-white bg-white/10'
                    : 'text-zinc-400 hover:text-white hover:bg-white/5'
                }`}
                data-testid={`nav-${item.id}`}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              className="text-sm text-zinc-300 hover:text-white"
              onClick={() => (isAuthenticated ? logout('/') : navigate('/login'))}
              data-testid="nav-login-btn"
            >
              {isAuthenticated ? 'Sign Out' : 'Sign In'}
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-5"
              onClick={() => handleSubscribe(selectedPlan)} data-testid="nav-get-started-btn">
              Get Started <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section
        ref={el => (sectionsRef.current['hero'] = el)}
        style={{ scrollMarginTop: '72px' }}
        className="relative min-h-screen flex items-center justify-center pt-20"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-blue-950/20 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-blue-900/10 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:72px_72px]" />

        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          {/* Eyebrow badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-8">
            <Users className="w-4 h-4 text-blue-400" />
            <span className="text-sm text-zinc-300">Trusted by 2,000+ funded traders worldwide</span>
          </div>

          <GlassTitle size="2xl" className="mb-6 leading-[0.9]">
            Your trading rules.<br />Executing themselves.
          </GlassTitle>

          <p className="text-lg md:text-xl text-zinc-400 max-w-2xl mx-auto mb-10 leading-relaxed">
            TradoPro automates your funded accounts end-to-end — strategy execution,
            risk enforcement, and multi-broker management, all from one dashboard.
            Pass evaluations faster. Keep accounts longer.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <Button
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg rounded-xl"
              onClick={() => handleSubscribe(selectedPlan)}
              data-testid="hero-get-started-btn"
            >
              Start Trading Free <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              size="lg" variant="outline"
              className="border-white/20 text-white hover:bg-white/5 px-8 py-6 text-lg rounded-xl"
              onClick={() => scrollToSection('how-it-works')}
            >
              See How it Works <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
          </div>

          {/* Stats bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-3xl mx-auto">
            {STATS.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-sm text-zinc-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll hint */}
        <div
          aria-hidden="true"
          className={`absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 transition-all duration-700 pointer-events-none ${
            showScrollHint ? 'opacity-40' : 'opacity-0'
          }`}
        >
          <div className="w-6 h-9 rounded-[10px] border-2 border-white/60 flex justify-center pt-1.5">
            <div className="w-1 h-2 rounded-full bg-white scroll-wheel" />
          </div>
          <span className="text-[10px] tracking-[0.2em] uppercase text-white/40">Scroll</span>
        </div>
      </section>

      {/* ── Trust / Social Proof Bar ── */}
      <div className="border-y border-white/5 bg-white/[0.015] py-10">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="text-xs text-zinc-600 uppercase tracking-[0.2em] mb-6">
            Trusted by funded traders on
          </p>
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-14">
            {TRUST_FIRMS.map(firm => (
              <span
                key={firm}
                className="text-zinc-400 font-semibold text-sm md:text-base opacity-50 hover:opacity-90 transition-opacity duration-200 select-none"
              >
                {firm}
              </span>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-xs text-zinc-600">
            <span className="flex items-center gap-1.5"><Lock className="w-3 h-3" /> Encrypted credentials</span>
            <span className="flex items-center gap-1.5"><Shield className="w-3 h-3" /> TLS 1.3 in transit</span>
            <span className="flex items-center gap-1.5"><RefreshCw className="w-3 h-3" /> 14-day money-back</span>
            <span className="flex items-center gap-1.5"><Zap className="w-3 h-3" /> Cancel anytime</span>
          </div>
        </div>
      </div>

      {/* ── Feature Pillars ── */}
      <section
        ref={el => (sectionsRef.current['features'] = el)}
        id="features"
        style={{ scrollMarginTop: '72px' }}
        className="py-24 md:py-32 relative"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 mb-4">
              <Zap className="w-3.5 h-3.5 text-blue-400" />
              <span className="text-xs text-blue-400 uppercase tracking-widest">Platform Capabilities</span>
            </div>
            <GlassTitle as="h2" size="lg" className="mb-4">Five capabilities. One unfair advantage.</GlassTitle>
            <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
              Everything a serious prop firm trader needs — built in, always on, no configuration debt.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
            {PILLARS.map((p, i) => (
              <div
                key={i}
                className="group p-6 rounded-2xl card-gloss hover:border-white/20 transition-all duration-300 relative"
              >
                {p.badge && (
                  <span className="absolute top-4 right-4 text-[10px] font-semibold tracking-wider uppercase px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/20">
                    {p.badge}
                  </span>
                )}
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-5 transition-colors ${p.bg}`}>
                  <p.icon className={`w-5 h-5 ${p.accent}`} />
                </div>
                <h3 className="text-base font-semibold mb-2">{p.title}</h3>
                <p className="text-sm text-zinc-500 leading-relaxed">{p.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it Works ── */}
      <section
        ref={el => (sectionsRef.current['how-it-works'] = el)}
        id="how-it-works"
        style={{ scrollMarginTop: '72px' }}
        className="py-24 md:py-32 bg-gradient-to-b from-transparent via-blue-950/5 to-transparent"
      >
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 mb-4">
              <BarChart3 className="w-3.5 h-3.5 text-purple-400" />
              <span className="text-xs text-purple-400 uppercase tracking-widest">Quick Setup</span>
            </div>
            <GlassTitle as="h2" size="lg" className="mb-4">From sign-up to automated in minutes</GlassTitle>
            <p className="text-lg text-zinc-400">No developer. No special software. Just your prop firm credentials and a strategy.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {STEPS.map((item, i) => (
              <div key={i} className="relative">
                <div className="text-7xl font-bold text-white/[0.04] mb-4 select-none">{item.step}</div>
                <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                <p className="text-zinc-500 leading-relaxed">{item.description}</p>
                {i < STEPS.length - 1 && (
                  <ChevronRight className="hidden md:block absolute top-10 -right-4 w-8 h-8 text-zinc-700" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section
        ref={el => (sectionsRef.current['testimonials'] = el)}
        id="testimonials"
        style={{ scrollMarginTop: '72px' }}
        className="py-24 md:py-32"
      >
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 mb-4">
              <Star className="w-3.5 h-3.5 text-amber-400" />
              <span className="text-xs text-amber-400 uppercase tracking-widest">Trader Stories</span>
            </div>
            <GlassTitle as="h2" size="lg" className="mb-4">Real traders. Real funded accounts.</GlassTitle>
            <p className="text-zinc-400 text-lg">What happens when your rules execute perfectly — every time, without emotion.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="p-6 rounded-2xl card-gloss flex flex-col gap-4">
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, s) => (
                    <Star key={s} className="w-4 h-4 text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <Quote className="w-5 h-5 text-zinc-600" />
                <p className="text-zinc-300 text-sm leading-relaxed flex-1">"{t.quote}"</p>
                <div>
                  <p className="font-semibold text-sm">{t.name}</p>
                  <p className="text-zinc-500 text-xs">{t.tag}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section
        ref={el => (sectionsRef.current['pricing'] = el)}
        id="pricing"
        style={{ scrollMarginTop: '72px' }}
        className="py-24 md:py-32 bg-gradient-to-b from-transparent via-blue-950/5 to-transparent"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/10 border border-green-500/20 mb-4">
              <CheckCircle2 className="w-3.5 h-3.5 text-green-400" />
              <span className="text-xs text-green-400 uppercase tracking-widest">Simple Pricing</span>
            </div>
            <GlassTitle as="h2" size="lg" className="mb-4">Start free. Automate everything.</GlassTitle>
            <p className="text-lg text-zinc-400 mb-8">No credit card required to start. Upgrade when your account count grows. Cancel anytime.</p>

            {/* Billing toggle */}
            <div className="inline-flex items-center gap-2 p-1.5 bg-white/5 rounded-xl">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  billingPeriod === 'monthly' ? 'bg-blue-600 text-white' : 'text-zinc-400 hover:text-white'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingPeriod('yearly')}
                className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                  billingPeriod === 'yearly' ? 'bg-blue-600 text-white' : 'text-zinc-400 hover:text-white'
                }`}
              >
                Yearly
                <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">
                  Save up to 17%
                </span>
              </button>
            </div>
          </div>

          {loadingPricing ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {pricingPlans.map((plan) => {
                const isSelected = selectedPlan === plan.name;
                const displayPrice = billingPeriod === 'yearly'
                  ? plan.price_yearly / 12
                  : plan.price_monthly;

                return (
                  <div
                    key={plan.name}
                    role="button"
                    tabIndex={0}
                    aria-pressed={isSelected}
                    onClick={() => setSelectedPlan(plan.name)}
                    onKeyDown={e => e.key === 'Enter' && setSelectedPlan(plan.name)}
                    className={`relative p-6 rounded-2xl border transition-all duration-200 cursor-pointer outline-none ${
                      isSelected
                        ? 'bg-blue-500/10 border-blue-500/70 shadow-[0_0_28px_rgba(59,130,246,0.22)] scale-[1.03]'
                        : plan.is_popular
                        ? 'bg-white/[0.04] border-zinc-500/60 scale-[1.01] hover:border-zinc-400/70'
                        : 'bg-white/[0.02] border-white/10 hover:border-white/25'
                    }`}
                  >
                    {plan.is_popular && !isSelected && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="bg-blue-600 text-white text-xs font-medium px-3 py-1 rounded-full">
                          Most Popular
                        </span>
                      </div>
                    )}
                    {isSelected && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="bg-blue-500 text-white text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> Selected
                        </span>
                      </div>
                    )}

                    <div className="text-center mb-6">
                      <h3 className="text-lg font-semibold mb-2">{plan.display_name}</h3>
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="text-4xl font-bold">
                          ${displayPrice > 0 ? displayPrice.toFixed(2) : '0'}
                        </span>
                        <span className="text-zinc-500 text-sm">/month</span>
                      </div>
                      {billingPeriod === 'yearly' && plan.price_yearly > 0 && (
                        <div className="text-xs text-green-400 mt-1">
                          ${plan.price_yearly}/year · save {plan.yearly_discount_percent}%
                        </div>
                      )}
                      <p className="text-sm text-zinc-500 mt-2">{plan.description}</p>
                    </div>

                    <ul className="space-y-3 mb-6">
                      {plan.features.map((feat, fi) => (
                        <li key={fi} className="flex items-center gap-2 text-sm">
                          <CheckCircle2 className="w-4 h-4 text-blue-400 flex-shrink-0" />
                          <span className="text-zinc-300">{feat}</span>
                        </li>
                      ))}
                    </ul>

                    <Button
                      className={`w-full transition-all ${
                        isSelected
                          ? 'bg-blue-600 hover:bg-blue-700 text-white'
                          : plan.is_popular
                          ? 'bg-blue-600 hover:bg-blue-700 text-white'
                          : 'bg-white/5 hover:bg-white/10 text-white border border-white/10'
                      }`}
                      onClick={e => { e.stopPropagation(); handleSubscribe(plan.name); }}
                    >
                      {isSelected ? <><span>Continue</span> <ArrowRight className="w-4 h-4 ml-1" /></> : plan.cta}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex flex-wrap items-center justify-center gap-6 mt-8 text-sm text-zinc-500">
            <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-green-500" /> 14-day money-back guarantee</span>
            <span className="flex items-center gap-1.5"><Lock className="w-4 h-4 text-zinc-500" /> Payments secured by Stripe</span>
            <span className="flex items-center gap-1.5"><RefreshCw className="w-4 h-4 text-zinc-500" /> Cancel anytime, no penalty</span>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section
        ref={el => (sectionsRef.current['faq'] = el)}
        id="faq"
        style={{ scrollMarginTop: '72px' }}
        className="py-24 md:py-32"
      >
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <GlassTitle as="h2" size="lg" className="mb-4">Every question, answered honestly</GlassTitle>
            <p className="text-zinc-400">Broker rules, data security, cancellations, refunds — nothing left vague.</p>
          </div>

          <Accordion type="single" collapsible className="space-y-3">
            {FAQ_ITEMS.map((item, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="border border-white/10 rounded-xl px-6 bg-white/[0.02] data-[state=open]:bg-white/[0.04] transition-colors"
              >
                <AccordionTrigger className="text-left font-medium text-sm py-4 hover:no-underline">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-zinc-400 text-sm leading-relaxed pb-4">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 via-blue-500/10 to-transparent" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Stop trading manually.<br className="hidden sm:block" /> Start trading at scale.
          </h2>
          <p className="text-lg text-zinc-400 mb-8 max-w-2xl mx-auto">
            Your edge is the strategy. TradoPro is the engine that executes it — across every funded account, 24/7, without emotion.
          </p>
          <Button
            size="lg"
            className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-6 text-lg rounded-xl"
            onClick={() => handleSubscribe(selectedPlan)}
            data-testid="cta-get-started-btn"
          >
            Get Started Free <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-white/10 py-12">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <Activity className="w-6 h-6 text-blue-500" />
            <span className="font-semibold">TradoPro</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-zinc-500">
            <a href="#" className="hover:text-white transition-colors">Terms</a>
            <a href="#" className="hover:text-white transition-colors">Privacy</a>
            <a href="#" className="hover:text-white transition-colors">Support</a>
          </div>
          <p className="text-sm text-zinc-600">© 2026 TradoPro. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
